import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * A bottom sheet for the result screen: the quality breakdown, the question
 * to the manager and a reviewer's reason. Text entry sits here rather than
 * in the page, so the keyboard covers the sheet and never the pinned
 * actions (plan/portal-final/DECISIONS.md item 7).
 *
 * The sheet takes `dvh`, the one place that unit is right: it is an overlay
 * and should follow the visible viewport as the browser bars come and go.
 * The bottom safe-area inset is applied here, because while the sheet is
 * open it is the bottom-most bar.
 *
 * Kept local to the review components until the shared sheet primitive
 * lands under `components/ui`; the props match a plain open, title,
 * close and body contract so the swap is a change of import.
 */
export function Sheet({ open, title, onClose, children }: SheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const first = panelRef.current?.querySelector<HTMLElement>(
      'textarea, input, button:not([data-sheet-close]), [href]',
    );
    (first ?? panelRef.current)?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/60" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className="mx-auto flex max-h-[85dvh] w-full max-w-md flex-col rounded-t-card border border-b-0 border-border-strong bg-surface sm:max-w-2xl focus-visible:outline-none"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex flex-none items-center justify-between gap-2 border-b border-border-strong py-1 pl-4 pr-1">
          <h2 id={titleId} className="text-sm font-semibold">
            {title}
          </h2>
          <button
            type="button"
            data-sheet-close
            onClick={onClose}
            aria-label="Close"
            className="flex size-11 items-center justify-center rounded-control text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
